import {
	RISK_PORTAL_READ,
	RISK_PORTAL_CREATE_AND_EDIT,
	RISK_PORTAL_DELETE,
	RISK_PORTAL_FULL_PERMISSIONS,
	PRIVACY_POLICIES_MODULE,
	PRIVACY_POLICIES_MODULE_FULL_PERMISSIONS,
	CAN_SEE_CONSENT_BANNERS,
	CAN_CREATE_UPDATE_CONSENT_BANNERS,
	CAN_DELETE_CONSENT_BANNERS
} from '../permissions/admin-portal-permissions.js'

/**
 * Set of all Risk Portal permission IDs for efficient lookup
 */
export const RISK_PORTAL_PERMISSIONS = new Set([
	RISK_PORTAL_READ,
	RISK_PORTAL_CREATE_AND_EDIT,
	RISK_PORTAL_DELETE,
	RISK_PORTAL_FULL_PERMISSIONS
])

/**
 * Set of all Privacy Notices permission IDs for efficient lookup
 */
export const PRIVACY_NOTICES_PERMISSIONS = new Set([
	PRIVACY_POLICIES_MODULE,
	PRIVACY_POLICIES_MODULE_FULL_PERMISSIONS
])

/**
 * Set of all Consent Banners permission IDs for efficient lookup
 */
export const CONSENT_BANNERS_PERMISSIONS = new Set([
	CAN_SEE_CONSENT_BANNERS,
	CAN_CREATE_UPDATE_CONSENT_BANNERS,
	CAN_DELETE_CONSENT_BANNERS
])

/**
 * Check if user has access to Risk Portal (permission-based only)
 * @param {Function} productAreaPermission - Vuex getter function to check permissions
 * @returns {boolean} True if user has any Risk Portal permission
 */
export function hasRiskPortalAccess (productAreaPermission) {
	return productAreaPermission(RISK_PORTAL_READ) ||
		productAreaPermission(RISK_PORTAL_CREATE_AND_EDIT) ||
		productAreaPermission(RISK_PORTAL_DELETE) ||
		productAreaPermission(RISK_PORTAL_FULL_PERMISSIONS)
}

/**
 * Check if user has access to Privacy Notices (config key + permissions)
 * @param {Function} productAreaPermission - Vuex getter function to check permissions
 * @param {boolean} privacyNoticeModuleEnabled - Config key value
 * @returns {boolean} True if user has Privacy Notices access
 */
export function hasPrivacyNoticesAccess (productAreaPermission, privacyNoticeModuleEnabled) {
	if (!privacyNoticeModuleEnabled) return false
	return productAreaPermission(PRIVACY_POLICIES_MODULE) ||
		productAreaPermission(PRIVACY_POLICIES_MODULE_FULL_PERMISSIONS)
}

/**
 * Shared permission predicate for the Consent Banners view set. Used by both
 * Consent Banners and Mobile Consent (which reuses the set until dedicated
 * Mobile Consent permissions exist) so the two cannot drift.
 * @param {Function} productAreaPermission - Vuex getter function to check permissions
 * @returns {boolean} True if user holds any Consent Banners view permission
 */
function hasConsentBannersViewPermission (productAreaPermission) {
	return productAreaPermission(CAN_SEE_CONSENT_BANNERS) ||
		productAreaPermission(CAN_CREATE_UPDATE_CONSENT_BANNERS) ||
		productAreaPermission(CAN_DELETE_CONSENT_BANNERS)
}

/**
 * Check if user has access to Consent Banners (config key + permissions)
 * @param {Function} productAreaPermission - Vuex getter function to check permissions
 * @param {boolean} consentBannersEnabled - nextGenBannerEnabled config key value (true when NextGenBannerProductSwitcherOptions is 1 or 2)
 * @returns {boolean} True if user has Consent Banners access
 */
export function hasConsentBannersAccess (productAreaPermission, consentBannersEnabled) {
	if (!consentBannersEnabled) return false
	return hasConsentBannersViewPermission(productAreaPermission)
}

/**
 * Check if user has access to Mobile Consent (config key + permissions)
 * Mobile Consent reuses the Consent Banners permission set until dedicated
 * Mobile Consent permissions exist.
 * @param {Function} productAreaPermission - Vuex getter function to check permissions
 * @param {boolean} mobileConsentEnabled - MobileConsentModuleEnabled config key value
 * @returns {boolean} True if user has Mobile Consent access
 */
export function hasMobileConsentAccess (productAreaPermission, mobileConsentEnabled) {
	if (!mobileConsentEnabled) return false
	return hasConsentBannersViewPermission(productAreaPermission)
}

/**
 * Count the number of products a user has access to
 * Admin Portal is always accessible (implicit count of 1)
 * @param {Function} productAreaPermission - Vuex getter function to check permissions
 * @param {boolean} privacyNoticeModuleEnabled - Config key value
 * @param {boolean} consentBannersEnabled - nextGenBannerEnabled config key value
 * @param {boolean} mobileConsentEnabled - MobileConsentModuleEnabled config key value
 * @returns {number} Number of accessible products (1-5)
 */
export function getAccessibleProductCount (productAreaPermission, privacyNoticeModuleEnabled, consentBannersEnabled = false, mobileConsentEnabled = false) {
	const hasRisk = hasRiskPortalAccess(productAreaPermission)
	const hasPrivacy = hasPrivacyNoticesAccess(productAreaPermission, privacyNoticeModuleEnabled)
	const hasConsent = hasConsentBannersAccess(productAreaPermission, consentBannersEnabled)
	const hasMobileConsent = hasMobileConsentAccess(productAreaPermission, mobileConsentEnabled)

	// Admin Portal (implicit) + Risk Portal + Privacy Notices + Consent Banners + Mobile Consent
	return 1 + (hasRisk ? 1 : 0) + (hasPrivacy ? 1 : 0) + (hasConsent ? 1 : 0) + (hasMobileConsent ? 1 : 0)
}

/**
 * Check if user has access to multiple products (2 or more)
 * Used to determine if splash page and product switcher should be shown
 * @param {Function} productAreaPermission - Vuex getter function to check permissions
 * @param {boolean} privacyNoticeModuleEnabled - Config key value
 * @param {boolean} consentBannersEnabled - nextGenBannerEnabled config key value
 * @param {boolean} mobileConsentEnabled - MobileConsentModuleEnabled config key value
 * @returns {boolean} True if user has access to 2+ products
 */
export function hasMultipleProductAccess (productAreaPermission, privacyNoticeModuleEnabled, consentBannersEnabled = false, mobileConsentEnabled = false) {
	return getAccessibleProductCount(productAreaPermission, privacyNoticeModuleEnabled, consentBannersEnabled, mobileConsentEnabled) >= 2
}

/**
 * Check if user ONLY has Risk Portal permissions (no other permissions)
 * Used for auto-redirect logic in splash page
 * @param {Array<number>} permissions - Array of user permission IDs
 * @returns {boolean} True if user only has Risk Portal permissions
 */
export function isRiskPortalOnly (permissions) {
	if (!Array.isArray(permissions) || !permissions.length) return false
	const allRisk = permissions.every(p => RISK_PORTAL_PERMISSIONS.has(p))
	const hasAtLeastOneRisk = permissions.some(p => RISK_PORTAL_PERMISSIONS.has(p))
	return allRisk && hasAtLeastOneRisk
}

/**
 * Check if user ONLY has Privacy Notices permissions (no other permissions)
 * Used for auto-redirect logic in splash page
 * @param {Array<number>} permissions - Array of user permission IDs
 * @param {boolean} privacyNoticeModuleEnabled - Config key value
 * @returns {boolean} True if user only has Privacy Notices permissions
 */
export function isPrivacyNoticesOnly (permissions, privacyNoticeModuleEnabled) {
	if (!privacyNoticeModuleEnabled) return false
	if (!Array.isArray(permissions) || !permissions.length) return false
	const allPrivacy = permissions.every(p => PRIVACY_NOTICES_PERMISSIONS.has(p))
	const hasAtLeastOnePrivacy = permissions.some(p => PRIVACY_NOTICES_PERMISSIONS.has(p))
	return allPrivacy && hasAtLeastOnePrivacy
}

/**
 * Check if user ONLY has Consent Banners permissions (no other permissions)
 * AND consent banners are enabled. Used for auto-redirect logic in splash page
 * @param {Array<number>} permissions - Array of user permission IDs
 * @param {boolean} consentBannersEnabled - nextGenBannerEnabled config key value
 * @returns {boolean} True if user only has Consent Banners permissions
 */
export function isConsentBannersOnly (permissions, consentBannersEnabled) {
	if (!consentBannersEnabled) return false
	if (!Array.isArray(permissions) || !permissions.length) return false
	const allConsent = permissions.every(p => CONSENT_BANNERS_PERMISSIONS.has(p))
	const hasAtLeastOneConsent = permissions.some(p => CONSENT_BANNERS_PERMISSIONS.has(p))
	return allConsent && hasAtLeastOneConsent
}
